import React, { useMemo, useState } from 'react';
import {
  BULK_OVERWRITE_USER_FIELDS,
  describeOverwriteColumn
} from '../../utils/bulkUserCsv';
import { getStudentInformationFieldDef } from '../../constants/studentInformationFields';

const REFERENCE_FIELDS = new Set([
  'track_period_id',
  'primary_pathway',
  'vendor_groups_periods_id'
]);
const USER_PROFILE_KEYS = new Set(BULK_OVERWRITE_USER_FIELDS.map((f) => f.key));

function formatPlainValue(value) {
  if (value === null || value === undefined || value === '') return 'empty';
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  return String(value);
}

function describeMatchMethod(fieldKey, sheetValue, matchMethod) {
  const sheet = String(sheetValue || '').trim();
  if (!sheet) return 'matched from the sheet';

  if (fieldKey === 'track_period_id') {
    if (matchMethod === 'letter_code') {
      return `found by matching sheet value "${sheet}" (letter code)`;
    }
    if (matchMethod === 'exact_title') {
      return `found by matching sheet value "${sheet}" (full title)`;
    }
    if (matchMethod === 'id') {
      return `found by matching sheet value "${sheet}" (id)`;
    }
    return `found by matching "${sheet}" on the sheet`;
  }

  if (fieldKey === 'primary_pathway') {
    if (matchMethod === 'exact_title') {
      return `found by matching sheet value "${sheet}" (pathway title)`;
    }
    if (matchMethod === 'id') {
      return `found by matching sheet value "${sheet}" (id)`;
    }
    return `found by matching "${sheet}" on the sheet`;
  }

  if (fieldKey === 'vendor_groups_periods_id') {
    if (matchMethod === 'group_name' || matchMethod === 'group_name_latest_period') {
      return `found by matching sheet value "${sheet}" on group name`;
    }
    if (matchMethod === 'id') {
      return `found by matching sheet value "${sheet}" (id)`;
    }
    return `found by matching "${sheet}" on the sheet`;
  }

  return `found by matching "${sheet}" on the sheet`;
}

function ReferenceBadge({ label, title }) {
  if (!label) return <span className="bulk-csv-overwrite-empty">empty</span>;
  return (
    <span className="bulk-csv-ref-badge" title={title || undefined}>
      <span className="bulk-csv-ref-badge-mark" aria-hidden="true">
        ↗
      </span>
      {label}
    </span>
  );
}

function ChangeValue({ change, side }) {
  const isRef = REFERENCE_FIELDS.has(change.field);
  if (!isRef) {
    return <span>{formatPlainValue(side === 'from' ? change.from : change.to)}</span>;
  }

  if (side === 'from') {
    if (change.from == null || change.from === '') {
      return <span className="bulk-csv-overwrite-empty">empty</span>;
    }
    return (
      <ReferenceBadge
        label={change.from_label || 'Selected'}
        title={change.from_label ? undefined : String(change.from)}
      />
    );
  }

  if (change.to == null || change.to === '') {
    return <span className="bulk-csv-overwrite-empty">empty</span>;
  }
  return (
    <ReferenceBadge
      label={change.to_label || 'Selected'}
      title={change.to_label ? undefined : String(change.to)}
    />
  );
}

function columnLabel(key) {
  return describeOverwriteColumn(key) || getStudentInformationFieldDef(key)?.label || key;
}

function ChangeLine({ change }) {
  const label = columnLabel(change.field);
  const isNewEnrollmentSet = change.action === 'set_on_new_enrollment';

  if (isNewEnrollmentSet) {
    return (
      <li>
        <span className="bulk-csv-overwrite-change-field">{label}:</span>{' '}
        <span className="bulk-csv-overwrite-change-set">will set to</span>{' '}
        <ChangeValue change={change} side="to" />
        <span className="bulk-csv-overwrite-change-note"> on the new enrollment</span>
      </li>
    );
  }

  return (
    <li>
      <span className="bulk-csv-overwrite-change-field">{label}:</span>{' '}
      <ChangeValue change={change} side="from" />
      <span className="bulk-csv-overwrite-change-arrow" aria-hidden="true">
        {' '}
        →{' '}
      </span>
      <ChangeValue change={change} side="to" />
      {change.scope === 'user_profile' ? (
        <span className="bulk-csv-overwrite-change-note"> (shared user profile)</span>
      ) : change.scope === 'student_information' ? (
        <span className="bulk-csv-overwrite-change-note"> (this period’s student info)</span>
      ) : null}
    </li>
  );
}

/**
 * Confirm actions for CSV rows whose emails already exist:
 * - optional user-profile overwrites
 * - new enrollment in this period (new UGP + student_info), or
 * - updates to an existing enrollment’s student_info
 */
const BulkCsvOverwriteConfirmPanel = ({
  candidates = [],
  selectedEmails = {},
  onToggleUser,
  overwriteColumns = {},
  onToggleColumn,
  extraColumnMappings = [],
  unchangedCount = 0,
  groupLabel = '',
  periodLabel = '',
  disabled = false
}) => {
  const [legendOpen, setLegendOpen] = useState(false);
  const [newEnrollmentsOpen, setNewEnrollmentsOpen] = useState(false);
  const [existingUpdatesOpen, setExistingUpdatesOpen] = useState(false);

  const periodScope = [groupLabel, periodLabel].filter(Boolean).join(' · ') || 'this period';

  const { profileColumnKeys, studentColumnKeys } = useMemo(() => {
    const profile = [...BULK_OVERWRITE_USER_FIELDS.map((f) => f.key)];
    const student = [];
    for (const mapping of extraColumnMappings || []) {
      if (!mapping.fieldKey) continue;
      if (USER_PROFILE_KEYS.has(mapping.fieldKey)) continue;
      if (!student.includes(mapping.fieldKey)) student.push(mapping.fieldKey);
    }
    return { profileColumnKeys: profile, studentColumnKeys: student };
  }, [extraColumnMappings]);

  const { newEnrollmentRows, existingUpdateRows } = useMemo(() => {
    const newRows = [];
    const updateRows = [];
    for (const row of candidates || []) {
      if (row.will_create_enrollment === true || row.already_enrolled === false) {
        newRows.push(row);
      } else {
        updateRows.push(row);
      }
    }
    return { newEnrollmentRows: newRows, existingUpdateRows: updateRows };
  }, [candidates]);

  const selectedCount = useMemo(
    () =>
      candidates.filter((row) => selectedEmails[String(row.email).toLowerCase()] !== false)
        .length,
    [candidates, selectedEmails]
  );

  const newEnrollmentCount = useMemo(
    () =>
      newEnrollmentRows.filter(
        (row) => selectedEmails[String(row.email).toLowerCase()] !== false
      ).length,
    [newEnrollmentRows, selectedEmails]
  );

  const updateEnrollmentCount = useMemo(
    () =>
      existingUpdateRows.filter(
        (row) => selectedEmails[String(row.email).toLowerCase()] !== false
      ).length,
    [existingUpdateRows, selectedEmails]
  );

  const mappingLegend = useMemo(() => {
    const byKey = new Map();
    for (const row of candidates || []) {
      for (const change of row.changes || []) {
        if (!REFERENCE_FIELDS.has(change.field)) continue;
        if (change.to == null || change.to === '') continue;
        const id = String(change.to);
        if (byKey.has(id)) continue;
        byKey.set(id, {
          id,
          fieldKey: change.field,
          label: change.to_label || 'Selected',
          sheetValue: change.sheet_value || null,
          matchMethod: change.match_method || null
        });
      }
      const maps = row.reference_maps || {};
      for (const ref of Object.values(maps)) {
        if (!ref?.id) continue;
        if (byKey.has(String(ref.id))) continue;
        byKey.set(String(ref.id), {
          id: String(ref.id),
          fieldKey: ref.fieldKey,
          label: ref.label || 'Selected',
          sheetValue: ref.sheetValue || null,
          matchMethod: ref.matchMethod || null
        });
      }
    }
    return [...byKey.values()].sort((a, b) =>
      String(a.label).localeCompare(String(b.label), undefined, { sensitivity: 'base' })
    );
  }, [candidates]);

  if (!candidates.length) return null;

  const renderColumnToggle = (key) => {
    const checked = overwriteColumns[key] !== false;
    const inputId = `bulk-overwrite-col-${key}`;
    return (
      <li key={key}>
        <label className="bulk-csv-option-row" htmlFor={inputId}>
          <input
            id={inputId}
            type="checkbox"
            checked={checked}
            onChange={(e) => onToggleColumn?.(key, e.target.checked)}
            disabled={disabled}
          />
          <span>{columnLabel(key)}</span>
        </label>
      </li>
    );
  };

  const renderPersonRow = (row) => {
    const emailKey = String(row.email).toLowerCase();
    const checked = selectedEmails[emailKey] !== false;
    const inputId = `bulk-overwrite-user-${row.row_number}-${emailKey}`;
    const name = [row.first_name, row.last_name].filter(Boolean).join(' ') || 'User';
    const willCreateEnrollment =
      row.will_create_enrollment === true || row.already_enrolled === false;
    return (
      <li key={`${row.row_number}-${emailKey}`}>
        <label className="bulk-csv-option-row" htmlFor={inputId}>
          <input
            id={inputId}
            type="checkbox"
            checked={checked}
            onChange={(e) => onToggleUser?.(emailKey, e.target.checked)}
            disabled={disabled}
          />
          <span className="bulk-csv-overwrite-user-main">
            <strong>{name}</strong>
            <span className="bulk-csv-overwrite-user-meta"> {row.email}</span>
            <span
              className={`bulk-csv-overwrite-enrollment-badge${
                willCreateEnrollment
                  ? ' bulk-csv-overwrite-enrollment-badge--new'
                  : ' bulk-csv-overwrite-enrollment-badge--existing'
              }`}
            >
              {willCreateEnrollment
                ? 'Will create new enrollment in this period'
                : 'Already enrolled in this period'}
            </span>
            {(row.changes || []).length > 0 ? (
              <ul className="bulk-csv-overwrite-change-list">
                {(row.changes || []).map((change) => (
                  <ChangeLine
                    key={`${row.row_number}-${change.field}-${change.action || 'change'}`}
                    change={change}
                  />
                ))}
              </ul>
            ) : willCreateEnrollment ? (
              <p className="bulk-csv-overwrite-enroll-only">
                No profile fields selected to change — will only add them to this period.
              </p>
            ) : null}
          </span>
        </label>
      </li>
    );
  };

  return (
    <div className="bulk-csv-overwrite-panel" role="region" aria-labelledby="bulk-csv-overwrite-title">
      <p className="profile-sheet-label" id="bulk-csv-overwrite-title">
        Existing accounts in this import
      </p>
      <p className="profile-sheet-hint">
        These emails already have a user account. Confirm each person, then choose what to apply:
      </p>
      <ul className="bulk-csv-overwrite-hint-list">
        <li>
          <strong>User profile</strong> fields (name, phone) update the shared account — only if
          checked and different.
        </li>
        <li>
          <strong>Student information</strong> (grade, track, vendor, …) belongs to an enrollment in{' '}
          <strong>{periodScope}</strong>. A prior-year enrollment is not overwritten when someone is
          added to a new period.
        </li>
        <li>
          If they are <strong>not already in this period</strong>, a <strong>new enrollment (UGP)</strong>{' '}
          is created and student-info values are set on that new enrollment.
        </li>
      </ul>
      {(newEnrollmentCount > 0 || updateEnrollmentCount > 0 || unchangedCount > 0) && (
        <p className="bulk-csv-overwrite-summary">
          {newEnrollmentCount > 0
            ? `${newEnrollmentCount} selected will get a new enrollment in this period`
            : null}
          {newEnrollmentCount > 0 && updateEnrollmentCount > 0 ? ' · ' : null}
          {updateEnrollmentCount > 0
            ? `${updateEnrollmentCount} selected already enrolled here (field updates only)`
            : null}
          {(newEnrollmentCount > 0 || updateEnrollmentCount > 0) && unchangedCount > 0
            ? ' · '
            : null}
          {unchangedCount > 0
            ? `${unchangedCount} already match and were omitted`
            : null}
          .
        </p>
      )}

      <div className="bulk-csv-overwrite-columns">
        <p className="bulk-csv-overwrite-subtitle">User profile columns (shared)</p>
        <p className="bulk-csv-overwrite-column-hint">
          Uncheck to leave the existing account name/phone unchanged.
        </p>
        <ul className="bulk-csv-overwrite-column-list">{profileColumnKeys.map(renderColumnToggle)}</ul>
      </div>

      {studentColumnKeys.length > 0 ? (
        <div className="bulk-csv-overwrite-columns">
          <p className="bulk-csv-overwrite-subtitle">Student information for this period</p>
          <p className="bulk-csv-overwrite-column-hint">
            For people not yet in this period, checked fields are set on the <em>new</em> enrollment —
            not copied over a previous year. For people already in this period, checked fields update
            that enrollment’s student info.
          </p>
          <ul className="bulk-csv-overwrite-column-list">{studentColumnKeys.map(renderColumnToggle)}</ul>
        </div>
      ) : null}

      {mappingLegend.length > 0 ? (
        <div className="bulk-csv-mapping-legend" role="note">
          <button
            type="button"
            className="bulk-csv-mapping-legend-toggle"
            aria-expanded={legendOpen}
            aria-controls="bulk-csv-mapping-legend-body"
            id="bulk-csv-mapping-legend-toggle"
            onClick={() => setLegendOpen((open) => !open)}
            disabled={disabled}
          >
            <span className="bulk-csv-mapping-legend-toggle-label">
              Reference mapping legend
              <span className="bulk-csv-mapping-legend-count">
                {mappingLegend.length} map{mappingLegend.length === 1 ? '' : 's'}
              </span>
            </span>
            <span className="bulk-csv-mapping-legend-chevron" aria-hidden="true">
              {legendOpen ? '▾' : '▸'}
            </span>
          </button>
          {legendOpen ? (
            <div id="bulk-csv-mapping-legend-body">
              <p className="bulk-csv-mapping-legend-hint">
                Badged values are linked records (not raw IDs). How each sheet value was matched:
              </p>
              <ul className="bulk-csv-mapping-legend-list">
                {mappingLegend.map((entry) => (
                  <li key={`${entry.fieldKey}-${entry.id}`}>
                    <ReferenceBadge label={entry.label} />
                    <span className="bulk-csv-mapping-legend-detail">
                      {columnLabel(entry.fieldKey)} —{' '}
                      {describeMatchMethod(entry.fieldKey, entry.sheetValue, entry.matchMethod)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="bulk-csv-overwrite-users">
        <p className="bulk-csv-overwrite-subtitle">
          People ({selectedCount} of {candidates.length} selected)
        </p>

        {newEnrollmentRows.length > 0 ? (
          <div className="bulk-csv-people-section">
            <button
              type="button"
              className="bulk-csv-people-section-toggle"
              aria-expanded={newEnrollmentsOpen}
              aria-controls="bulk-csv-new-enrollments-body"
              id="bulk-csv-new-enrollments-toggle"
              onClick={() => setNewEnrollmentsOpen((open) => !open)}
              disabled={disabled}
            >
              <span className="bulk-csv-people-section-toggle-label">
                New enrollments (UGP)
                <span className="bulk-csv-people-section-count">
                  {newEnrollmentCount} of {newEnrollmentRows.length} selected
                </span>
              </span>
              <span className="bulk-csv-people-section-chevron" aria-hidden="true">
                {newEnrollmentsOpen ? '▾' : '▸'}
              </span>
            </button>
            {newEnrollmentsOpen ? (
              <ul
                id="bulk-csv-new-enrollments-body"
                className="bulk-csv-overwrite-user-list"
                aria-labelledby="bulk-csv-new-enrollments-toggle"
              >
                {newEnrollmentRows.map(renderPersonRow)}
              </ul>
            ) : null}
          </div>
        ) : null}

        {existingUpdateRows.length > 0 ? (
          <div className="bulk-csv-people-section">
            <button
              type="button"
              className="bulk-csv-people-section-toggle"
              aria-expanded={existingUpdatesOpen}
              aria-controls="bulk-csv-existing-updates-body"
              id="bulk-csv-existing-updates-toggle"
              onClick={() => setExistingUpdatesOpen((open) => !open)}
              disabled={disabled}
            >
              <span className="bulk-csv-people-section-toggle-label">
                Already enrolled — field updates
                <span className="bulk-csv-people-section-count">
                  {updateEnrollmentCount} of {existingUpdateRows.length} selected
                </span>
              </span>
              <span className="bulk-csv-people-section-chevron" aria-hidden="true">
                {existingUpdatesOpen ? '▾' : '▸'}
              </span>
            </button>
            {existingUpdatesOpen ? (
              <ul
                id="bulk-csv-existing-updates-body"
                className="bulk-csv-overwrite-user-list"
                aria-labelledby="bulk-csv-existing-updates-toggle"
              >
                {existingUpdateRows.map(renderPersonRow)}
              </ul>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default BulkCsvOverwriteConfirmPanel;
